import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { motion, MotionConfig } from "motion/react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Award,
  BadgeCheck,
  Bot,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Coffee,
  Database,
  GraduationCap,
  ListChecks,
  MailCheck,
  MapPin,
  Search,
  Send,
  ShieldCheck,
  Star,
  Target,
  Terminal,
} from "lucide-react";

const jobs = [
  {
    id: "database-architects",
    icon: Database,
    title: "Database Architects – Multiple Openings",
    location: "Cumming, GA, USA",
    intro:
      "Comsense Technologies LLC is seeking experienced Database Architects to join our team.",
    responsibilities: [
      "Work on KPI reporting and data analysis",
      "Develop and manage ETL processes",
      "Write and optimize SQL queries",
      "Optimize data loading and database performance",
      "Troubleshoot and resolve data-related issues",
    ],
    requirements: [
      "Bachelor’s degree plus 5 years of experience required",
      "Relocation may be required based on client needs",
      "No domestic or international travel required",
    ],
    contact: {
      title: "Director",
      company: "Comsense Technologies LLC",
      addressLines: ["3135 Olde Cornell Ct", "Cumming, GA 30041"],
      emails: ["milind@comsenseconsulting.com", "recruit@comsenseconsulting.com"],
    },
  },
  {
    id: "sfmc-qa",
    icon: ShieldCheck,
    shortTitle: ["Salesforce QA Engineer", "Marketing Cloud"],
    title: "Salesforce Marketing Cloud QA Engineer",
    location: "Pune",
    intro:
      "Comsense Technologies is hiring a Salesforce Marketing Cloud QA Engineer with 3+ years of experience.",
    responsibilities: [
      "Perform functional, regression, and end-to-end testing of SFMC solutions",
      "Validate email campaigns, customer journeys, automation workflows, and data extensions",
      "Test Journey Builder, Email Studio, Automation Studio, and Contact Builder functionalities",
      "Perform SQL-based data validation and API testing",
      "Review AMPscript and SSJS logic used in campaigns",
      "Support UAT, defect tracking, and QA processes",
      "Collaborate with developers, analysts, and stakeholders to ensure solution quality",
    ],
    requirements: [
      "Hands-on experience with Salesforce Marketing Cloud testing",
      "Strong knowledge of SQL, AMPscript, and SSJS",
      "Experience with API testing tools such as Postman",
      "Familiarity with QA methodologies, Agile, and defect lifecycle management",
    ],
    meta: [
      { label: "Experience", value: "3+ Years" },
      { label: "Joining", value: "Immediate to 15 Days" },
    ],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com"],
    },
  },
  {
    id: "agentic-ai",
    icon: Bot,
    title: "AI/ML Engineer – Agentic AI Specialist",
    location: "Pune | Work from Office",
    intro:
      "Comsense Technologies is hiring an AI/ML Engineer with 4+ years of experience in building production-grade AI solutions.",
    responsibilities: [
      "Design and develop Agentic AI systems, AI copilots, and intelligent automation workflows",
      "Build and optimize RAG pipelines, semantic search, and conversational AI solutions",
      "Fine-tune LLMs using LoRA, QLoRA, instruction tuning, and RLHF techniques",
      "Develop NLP-to-SQL systems, OCR, and Computer Vision applications",
      "Build REST APIs and backend services using Python and FastAPI",
      "Integrate third-party APIs such as Meta Ads, DV360, and Google Ads",
      "Deploy AI applications on AWS or GCP cloud platforms",
      "Work on predictive modelling, recommendation systems, and analytics projects",
    ],
    requirements: [
      "Python, SQL, FastAPI, LangChain, LangGraph",
      "Pinecone/ChromaDB, Transformers, OpenCV, scikit-learn, XGBoost",
      "Prompt Engineering, LLM Optimization, GCP/AWS",
    ],
    meta: [
      { label: "Experience", value: "4+ Years" },
      { label: "Joining", value: "15 to 30 Days" },
    ],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com"],
    },
  },
  {
    id: "java-fresher",
    icon: Coffee,
    level: "fresher",
    title: "Java Developer – Fresher",
    location: "Pune",
    intro:
      "We are seeking enthusiastic Java Developer freshers with a strong foundation in programming and an interest in developing enterprise-level applications. The candidate will work with experienced developers to build, test, and maintain Java-based software solutions.",
    qualification:
      "B.E./B.Tech/B.Sc./M.Sc./MCA in Computer Science, IT, or a related field",
    responsibilities: [
      "Develop and maintain applications using Java",
      "Apply object-oriented programming principles to development tasks",
      "Write clean, maintainable, and reusable code",
      "Work with databases and perform basic SQL queries",
      "Assist in developing and integrating RESTful APIs",
      "Participate in debugging, testing, and resolving software issues",
      "Collaborate with team members throughout the software development life cycle",
      "Follow coding standards, development guidelines, and best practices",
    ],
    requirementsTitle: "Required Skills",
    requirements: [
      "Basic to good knowledge of Core Java",
      "Understanding of OOPs, collections, exception handling, and multithreading fundamentals",
      "Familiarity with SQL and relational databases",
      "Basic understanding of REST APIs and HTTP concepts",
      "Familiarity with Git or other version control tools",
      "Strong logical reasoning and problem-solving skills",
    ],
    preferredSkills: [
      "Basic knowledge of Spring Boot and Hibernate/JPA",
      "Familiarity with Maven or Gradle",
      "Understanding of JDBC and database connectivity",
      "Academic projects or internships using Java",
    ],
    eligibility:
      "Candidates should demonstrate strong Java fundamentals, a willingness to learn modern development frameworks, and the ability to work effectively in a team.",
    meta: [{ label: "Experience", value: "0–1 Year" }],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com", "recruit@comsenseconsulting.com"],
    },
  },
  {
    id: "python-fresher",
    icon: Terminal,
    level: "fresher",
    title: "Python Developer – Fresher",
    location: "Pune",
    intro:
      "We are looking for enthusiastic and motivated Python Developer freshers who are eager to build their careers in software development. The candidate will work with the development team to design, develop, test, and maintain applications using Python.",
    qualification:
      "B.E./B.Tech/B.Sc./M.Sc./MCA in Computer Science, IT, or a related field",
    responsibilities: [
      "Develop, test, and maintain applications using Python",
      "Write clean, efficient, reusable, and well-documented code",
      "Understand programming concepts, including OOPs, data structures, and algorithms",
      "Work with databases such as MySQL or PostgreSQL",
      "Understand and work with REST APIs",
      "Identify and troubleshoot bugs and assist in application debugging",
      "Collaborate with team members to deliver assigned development tasks",
      "Learn new technologies, frameworks, and development best practices",
    ],
    requirementsTitle: "Required Skills",
    requirements: [
      "Basic to good knowledge of Python programming",
      "Understanding of OOPs, data structures, and algorithms",
      "Familiarity with SQL and relational databases",
      "Basic understanding of APIs, Git, and version control",
      "Knowledge of HTML, CSS, or JavaScript is an added advantage",
      "Strong analytical, problem-solving, and communication skills",
    ],
    preferredSkills: [
      "Familiarity with Django, Flask, or FastAPI",
      "Academic projects or internships involving Python",
      "Basic understanding of software development life cycle (SDLC)",
    ],
    eligibility:
      "Candidates should have a strong programming foundation, a willingness to learn, and an interest in building scalable software applications.",
    meta: [{ label: "Experience", value: "0–1 Year" }],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com", "recruit@comsenseconsulting.com"],
    },
  },
  {
    id: "ai-ml-fresher",
    icon: BrainCircuit,
    level: "fresher",
    title: "AI/ML Engineer – Fresher",
    location: "Pune",
    intro:
      "We are looking for motivated AI/ML Engineer freshers interested in artificial intelligence, machine learning, and data-driven solutions. The candidate will support the development, testing, and evaluation of AI/ML models and contribute to building intelligent applications.",
    qualification:
      "B.E./B.Tech/B.Sc./M.Sc./MCA in Computer Science, Artificial Intelligence, Machine Learning, Data Science, or a related field",
    responsibilities: [
      "Assist in developing and testing machine learning models",
      "Collect, clean, preprocess, and analyze datasets",
      "Apply basic statistical and machine learning techniques to solve problems",
      "Use Python libraries to support data analysis and model development",
      "Evaluate model performance using appropriate metrics",
      "Assist in integrating AI/ML models into applications and workflows",
      "Explore generative AI tools, large language models (LLMs), and prompt engineering",
      "Document experiments, results, and model performance",
      "Collaborate with the team to research and implement AI-based solutions",
    ],
    requirementsTitle: "Required Skills",
    requirements: [
      "Basic knowledge of Python programming",
      "Understanding of fundamental statistics, probability, and linear algebra",
      "Familiarity with machine learning concepts such as regression, classification, clustering, and model evaluation",
      "Basic knowledge of libraries such as NumPy, Pandas, and Scikit-learn",
      "Understanding of data preprocessing and exploratory data analysis",
      "Strong analytical, logical reasoning, and problem-solving skills",
    ],
    preferredSkills: [
      "Basic exposure to TensorFlow or PyTorch",
      "Familiarity with generative AI, LLMs, and prompt engineering",
      "Basic understanding of NLP or computer vision",
      "Familiarity with APIs, Git, and Jupyter Notebook",
      "Academic projects, internships, or certifications in AI/ML",
    ],
    eligibility:
      "Candidates should have a strong interest in AI/ML, a willingness to learn emerging technologies, and the ability to apply fundamental programming and analytical concepts to practical problems.",
    meta: [{ label: "Experience", value: "0–1 Year" }],
    contact: {
      emails: ["shubhamk@comsenseconsulting.com", "recruit@comsenseconsulting.com"],
    },
  },
];

const filters = [
  { id: "all", label: "All Roles" },
  { id: "experienced", label: "Experienced" },
  { id: "fresher", label: "Freshers" },
];

const steps = [
  {
    icon: Search,
    title: "Find your role",
    text: "Browse the openings and expand a role to see what it involves.",
  },
  {
    icon: Send,
    title: "Send your resume",
    text: "Use the Apply button or email the contact listed in the posting.",
  },
  {
    icon: MailCheck,
    title: "Hear from us",
    text: "Our team reviews your application and reaches out about next steps.",
  },
];

const metaIcons = { Experience: Award, Joining: CalendarDays };

const isFresher = (job) => job.level === "fresher";

const getShortTitle = (job) => job.shortTitle || job.title.split(" – ");

const matchesFilter = (job, filter) =>
  filter === "all" || (filter === "fresher") === isFresher(job);

const getApplyHref = (job) =>
  `mailto:${job.contact.emails.join(",")}?subject=${encodeURIComponent(
    `Application for ${job.title}`
  )}`;

const Careers = () => {
  const [filter, setFilter] = useState("all");
  const [openIds, setOpenIds] = useState([jobs[0].id]);
  const [scrollTargetId, setScrollTargetId] = useState(null);

  // The target card may only exist once the filter change has rendered.
  useEffect(() => {
    if (!scrollTargetId) return;
    document
      .getElementById(`job-${scrollTargetId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollTargetId(null);
  }, [scrollTargetId]);

  const toggleJob = (id) => {
    setOpenIds((current) =>
      current.includes(id)
        ? current.filter((openId) => openId !== id)
        : [...current, id]
    );
  };

  const showJob = (id) => {
    setFilter("all");
    setOpenIds((current) => (current.includes(id) ? current : [...current, id]));
    setScrollTargetId(id);
  };

  const scrollToRoles = () => {
    document
      .getElementById("open-roles")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MotionConfig reducedMotion="user">
      <Helmet>
        <title>Careers | Comsense Technologies</title>
        <meta
          name="description"
          content="Explore current job openings at Comsense Technologies. Apply for roles across Data, Salesforce Marketing Cloud QA, AI/ML engineering, and Java and Python development."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Careers | Comsense Technologies" />
        <meta
          property="og:description"
          content="Explore current job openings at Comsense Technologies."
        />
        <meta
          property="og:url"
          content="https://comsensetechnologies.com/careers"
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');

        .careers-page, .careers-page * {
          font-family: 'Outfit', sans-serif;
        }

        .careers-page {
          background: #fff;
          overflow: hidden;
        }

        .careers-dot-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(#b9cfe0 1px, transparent 1px);
          background-size: 24px 24px;
          opacity: 0.45;
          pointer-events: none;
        }

        /* Hero */
        .careers-hero {
          position: relative;
          padding: 80px 0;
          background: #e2f1fe;
          overflow: hidden;
        }

        .careers-hero-inner {
          position: relative;
          z-index: 1;
        }

        .careers-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          font-size: 12px;
          font-weight: 700;
          color: #0f172a;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .careers-live-dot {
          width: 8px;
          height: 8px;
          flex-shrink: 0;
          background: #ee0000;
          border-radius: 50%;
          animation: careersDot 2s ease-out infinite;
        }

        @keyframes careersDot {
          0% { box-shadow: 0 0 0 0 rgba(238, 0, 0, 0.5); }
          100% { box-shadow: 0 0 0 9px rgba(238, 0, 0, 0); }
        }

        .careers-title {
          margin: 20px 0 16px;
          font-size: 58px;
          font-weight: 800;
          line-height: 1.08;
          letter-spacing: -0.025em;
          color: #0f172a;
        }

        .careers-highlight {
          display: block;
          color: #ee0000;
        }

        .careers-subtitle {
          margin: 0;
          max-width: 560px;
          color: #475569;
          font-size: 18px;
          line-height: 30px;
        }

        .careers-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-top: 28px;
          padding: 14px 26px;
          border: none;
          border-radius: 999px;
          background: #ee0000;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          box-shadow: 0 20px 40px -20px rgba(238, 0, 0, 0.7);
          transition: transform 0.2s ease;
        }

        .careers-cta:hover {
          transform: translateY(-2px);
        }

        /* Now hiring panel */
        .hiring-wrap {
          position: relative;
          margin-top: 18px;
        }

        .hiring-wrap::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 26px;
          background: rgba(6, 123, 177, 0.2);
          transform: rotate(2.5deg);
        }

        .hiring-panel {
          position: relative;
          padding: 38px 16px 16px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          border-radius: 26px;
          box-shadow: 0 30px 60px -24px rgba(4, 70, 122, 0.7);
          overflow: hidden;
        }

        .hiring-panel::before {
          content: "";
          position: absolute;
          width: 150px;
          height: 150px;
          right: -55px;
          bottom: -65px;
          border: 22px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }

        .hiring-badge {
          position: absolute;
          top: -18px;
          left: 24px;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 18px;
          border-radius: 999px;
          background: #fff;
          color: #0f172a;
          font-size: 15px;
          font-weight: 700;
          box-shadow: 0 14px 28px -12px rgba(4, 70, 122, 0.65);
          animation: hiringFloat 4s ease-in-out infinite;
        }

        @keyframes hiringFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .hiring-grid {
          position: relative;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .hiring-role {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          text-align: left;
          transition: background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
        }

        .hiring-role:hover {
          background: #fff;
          color: #0f172a;
          box-shadow: 0 18px 30px -16px rgba(0, 0, 0, 0.55);
        }

        .hiring-role-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border-radius: 11px;
          background: #fff;
          color: #ee0000;
          transition: background 0.25s ease, color 0.25s ease;
        }

        .hiring-role:hover .hiring-role-icon {
          background: #ee0000;
          color: #fff;
        }

        .hiring-role-body {
          display: flex;
          flex: 1;
          flex-direction: column;
          min-width: 0;
        }

        .hiring-role-title {
          font-size: 14px;
          font-weight: 700;
          line-height: 1.3;
        }

        .hiring-role-sub {
          margin-top: 2px;
          color: #e2f1fe;
          font-size: 12px;
          line-height: 1.3;
          transition: color 0.25s ease;
        }

        .hiring-role:hover .hiring-role-sub {
          color: #475569;
        }

        .careers-page .hiring-role:focus-visible {
          outline-color: #fff;
        }

        @media (max-width: 480px) {
          .hiring-grid { grid-template-columns: minmax(0, 1fr); }
          .hiring-wrap::before { transform: rotate(1.5deg); }
        }

        /* Open roles */
        .careers-roles {
          position: relative;
          padding: 80px 0;
          scroll-margin-top: 90px;
        }

        .careers-roles-head {
          position: relative;
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 32px;
        }

        .careers-section-title {
          margin: 0;
          font-size: 42px;
          font-weight: 800;
          letter-spacing: -0.5px;
          color: #0f172a;
        }

        .careers-section-sub {
          margin: 8px 0 0;
          color: #475569;
          font-size: 16px;
        }

        .careers-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 6px;
          background: #f1f5f9;
          border-radius: 999px;
        }

        .careers-filter {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
          transition: background 0.2s ease, color 0.2s ease;
        }

        .careers-filter:hover {
          color: #0f172a;
        }

        .careers-filter.active {
          background: #ee0000;
          color: #fff;
        }

        .careers-filter-count {
          font-size: 12px;
          font-weight: 600;
          opacity: 0.8;
        }

        .job-list-stack {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .job-card {
          position: relative;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          box-shadow: 0 8px 18px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          scroll-margin-top: 110px;
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }

        .job-card:not(.open):hover {
          transform: translateY(-3px);
          box-shadow: 0 22px 40px -18px rgba(15, 23, 42, 0.28);
        }

        .job-card.open {
          border-color: transparent;
          box-shadow: 0 30px 60px -28px rgba(15, 23, 42, 0.45);
        }

        .job-heading {
          margin: 0;
        }

        .job-toggle {
          position: relative;
          display: flex;
          align-items: center;
          gap: 18px;
          width: 100%;
          padding: 24px 28px;
          border: none;
          background: transparent;
          text-align: left;
          overflow: hidden;
        }

        .job-toggle::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #e2f1fe 0%, #effdff 100%);
          opacity: 0;
          transition: opacity 0.35s ease;
        }

        .job-card.open .job-toggle::before {
          opacity: 1;
        }

        .job-toggle > * {
          position: relative;
        }

        .job-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          flex-shrink: 0;
          border-radius: 16px;
          background: #fff5f5;
          border: 1px solid #ffd4d4;
          color: #ee0000;
          transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }

        .job-card:hover .job-icon, .job-card.open .job-icon {
          background: #ee0000;
          border-color: #ee0000;
          color: #fff;
        }

        .job-card.open .job-icon {
          box-shadow: 0 12px 28px -8px rgba(238, 0, 0, 0.8);
        }

        .job-toggle-body {
          flex: 1;
          min-width: 0;
        }

        .job-title {
          display: block;
          margin-bottom: 10px;
          color: #0f172a;
          font-size: 21px;
          font-weight: 800;
          line-height: 1.3;
          letter-spacing: -0.01em;
          transition: color 0.3s ease;
        }

        .job-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .job-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 999px;
          background: #f1f5f9;
          color: #0f172a;
          font-size: 13px;
          font-weight: 500;
          transition: background 0.3s ease, color 0.3s ease;
        }

        .job-card.open .job-meta-item {
          background: #fff;
        }

        .job-meta-item.fresher {
          background: #fee2e2;
          color: #b80000;
          font-weight: 700;
        }

        .job-card.open .job-meta-item.fresher {
          background: #fee2e2;
        }

        .job-chevron {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          flex-shrink: 0;
          border-radius: 50%;
          background: #f1f5f9;
          color: #0f172a;
          transition: transform 0.3s ease, background 0.3s ease;
        }

        .job-card.open .job-chevron {
          transform: rotate(180deg);
          background: #fff;
        }

        .job-panel {
          display: grid;
          grid-template-rows: 0fr;
          visibility: hidden;
          transition: grid-template-rows 0.35s ease, visibility 0.35s;
        }

        .job-card.open .job-panel {
          grid-template-rows: 1fr;
          visibility: visible;
        }

        .job-panel-inner {
          min-height: 0;
          overflow: hidden;
        }

        .job-details {
          padding: 28px;
        }

        .job-intro {
          margin: 0;
          color: #475569;
          font-size: 14px;
          line-height: 23px;
        }

        .job-summary {
          max-width: 920px;
          margin-bottom: 28px;
          color: #0f172a;
          font-size: 17px;
          line-height: 29px;
        }

        .job-columns {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 330px;
          gap: 32px;
          align-items: start;
        }

        .job-block-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 16px;
          color: #0f172a;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }

        .job-block-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          border-radius: 10px;
          background: #fff5f5;
          color: #ee0000;
        }

        .job-block + .job-block {
          margin-top: 26px;
        }

        .job-timeline, .job-tiles, .job-pills {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .job-timeline li {
          position: relative;
          padding: 0 0 16px 26px;
          color: #475569;
          font-size: 14px;
          line-height: 22px;
        }

        .job-timeline li:last-child {
          padding-bottom: 0;
        }

        .job-timeline li::before {
          content: "";
          position: absolute;
          top: 6px;
          left: 0;
          width: 11px;
          height: 11px;
          border: 2.5px solid #ee0000;
          border-radius: 50%;
          background: #fff;
          transition: background 0.2s ease;
          z-index: 1;
        }

        .job-timeline li::after {
          content: "";
          position: absolute;
          top: 12px;
          bottom: -6px;
          left: 4.5px;
          width: 2px;
          background: #fee2e2;
        }

        .job-timeline li:last-child::after {
          display: none;
        }

        .job-timeline li:hover::before {
          background: #ee0000;
        }

        .job-tiles {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .job-tiles li {
          display: flex;
          gap: 10px;
          padding: 10px 14px;
          border: 1px solid #eef2f7;
          border-radius: 14px;
          background: #f8fafc;
          color: #334155;
          font-size: 14px;
          line-height: 21px;
          transition: transform 0.2s ease, background 0.2s ease;
        }

        .job-tiles li:hover {
          transform: translateX(4px);
          background: #effdff;
        }

        .job-tiles svg {
          flex-shrink: 0;
          margin-top: 3px;
          color: #ee0000;
        }

        .job-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .job-pills li {
          padding: 7px 13px;
          border-radius: 14px;
          background: #e2f1fe;
          color: #0f172a;
          font-size: 13px;
          font-weight: 500;
          line-height: 19px;
        }

        .job-aside {
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-width: 0;
        }

        .job-info-card {
          padding: 20px;
          background: #effdff;
          border: 1px solid #d5eef5;
          border-radius: 20px;
        }

        .job-info-card .job-block-title {
          margin-bottom: 10px;
        }

        .job-info-card .job-block-icon {
          background: #fff;
        }

        .job-contact {
          position: relative;
          padding: 22px 20px 20px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          border-radius: 20px;
          color: #fff;
          font-size: 14px;
          line-height: 23px;
          overflow: hidden;
          overflow-wrap: anywhere;
        }

        .job-contact::before {
          content: "";
          position: absolute;
          width: 170px;
          height: 170px;
          top: -70px;
          right: -60px;
          border: 24px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }

        .job-contact > * {
          position: relative;
        }

        .job-contact-lead {
          margin-bottom: 8px;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          line-height: 23px;
        }

        .job-contact a {
          color: #fff;
          font-weight: 600;
          text-decoration: none;
        }

        .job-contact a:hover {
          text-decoration: underline;
        }

        .job-contact a.apply-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 16px;
          padding: 13px 18px;
          border-radius: 999px;
          background: #ee0000;
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 16px 30px -14px rgba(238, 0, 0, 0.9);
          transition: transform 0.2s ease;
        }

        .job-contact a.apply-btn:hover {
          transform: translateY(-2px);
        }

        .apply-btn svg {
          transition: transform 0.2s ease;
        }

        .apply-btn:hover svg {
          transform: translate(3px, -3px);
        }

        /* How to apply */
        .careers-steps {
          position: relative;
          padding: 80px 0;
          background: #e2f1fe;
          overflow: hidden;
        }

        .careers-steps-inner {
          position: relative;
          z-index: 1;
        }

        .careers-steps .careers-section-title {
          margin-bottom: 48px;
          text-align: center;
        }

        .steps-track {
          position: relative;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 24px;
        }

        .steps-line {
          position: absolute;
          top: 35px;
          left: calc(16.666% - 8px);
          width: calc(66.666% + 16px);
          border-top: 2px dashed rgba(6, 123, 177, 0.5);
        }

        .steps-line-dot {
          position: absolute;
          top: -7px;
          left: 0;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #ee0000;
          box-shadow: 0 0 12px 2px rgba(238, 0, 0, 0.55);
          animation: stepsTravel 6s linear infinite;
        }

        @keyframes stepsTravel {
          from { left: 0; }
          to { left: 100%; }
        }

        .step-item {
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .step-node {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 72px;
          height: 72px;
          flex-shrink: 0;
          align-self: center;
          margin-bottom: 24px;
          border-radius: 50%;
          background: #fff;
          color: #ee0000;
          box-shadow: 0 0 0 8px rgba(6, 123, 177, 0.15);
          transition: transform 0.3s ease, background 0.3s ease, color 0.3s ease;
        }

        .step-item:hover .step-node {
          transform: scale(1.08);
          background: #ee0000;
          color: #fff;
        }

        .step-card {
          position: relative;
          flex: 1;
          padding: 26px 24px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          border-radius: 22px;
          box-shadow: 0 20px 40px -22px rgba(4, 70, 122, 0.75);
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .step-card::after {
          content: "";
          position: absolute;
          width: 130px;
          height: 130px;
          right: -50px;
          bottom: -60px;
          border: 20px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }

        .step-item:hover .step-card {
          transform: translateY(-4px);
          box-shadow: 0 28px 48px -22px rgba(4, 70, 122, 0.9);
        }

        .step-title {
          position: relative;
          margin: 0 0 8px;
          color: #fff;
          font-size: 20px;
          font-weight: 800;
        }

        .step-text {
          position: relative;
          margin: 0;
          color: #e2f1fe;
          font-size: 15px;
          line-height: 26px;
        }

        .steps-cta {
          display: flex;
          justify-content: center;
          margin-top: 12px;
        }

        .careers-page button:focus-visible, .careers-page a:focus-visible {
          outline: 3px solid #067bb1;
          outline-offset: 2px;
        }

        @media (max-width: 767px) {
          .steps-track { grid-template-columns: minmax(0, 1fr); gap: 20px; }
          .steps-line {
            top: 28px;
            bottom: 110px;
            left: 27px;
            width: 0;
            border-top: none;
            border-left: 2px dashed rgba(6, 123, 177, 0.5);
          }
          .steps-line-dot { left: -7px; animation-name: stepsTravelDown; }
          .step-item { flex-direction: row; gap: 16px; }
          .step-node { align-self: flex-start; width: 56px; height: 56px; margin-bottom: 0; }
          .careers-steps .careers-section-title { margin-bottom: 32px; }
        }

        @keyframes stepsTravelDown {
          from { top: 0; }
          to { top: 100%; }
        }

        @media (max-width: 1199px) {
          .job-columns { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
          .job-aside { grid-column: 1 / -1; }
        }

        @media (max-width: 991px) {
          .careers-hero { padding: 60px 0; }
          .careers-title { font-size: 44px; }
          .careers-roles, .careers-steps { padding: 60px 0; }
          .careers-section-title { font-size: 34px; }
        }

        @media (max-width: 767px) {
          .careers-title { font-size: 38px; }
          .careers-subtitle { font-size: 16px; line-height: 28px; }
          .careers-section-title { font-size: 30px; }
          .job-columns { grid-template-columns: minmax(0, 1fr); }
          .job-toggle { align-items: flex-start; gap: 12px; padding: 18px; }
          .job-icon { width: 42px; height: 42px; border-radius: 12px; }
          .job-title { font-size: 18px; }
          .job-chevron { width: 32px; height: 32px; }
          .job-details { padding: 20px 18px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .careers-page *, .careers-page *::before {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="careers-page">
        <section className="careers-hero">
          <div className="careers-dot-grid" />
          <Container className="careers-hero-inner">
            <Row className="align-items-center g-4 g-lg-5">
              <Col lg={7}>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55 }}
                >
                  <div className="careers-badge">
                    <span className="careers-live-dot" />
                    Careers at Comsense
                  </div>
                  <h1 className="careers-title">
                    Join Comsense.
                    <span className="careers-highlight">Build what's next.</span>
                  </h1>
                  <p className="careers-subtitle">
                    Explore our current openings and apply by emailing your
                    resume to the contact listed in each posting.
                  </p>
                  <button
                    type="button"
                    className="careers-cta"
                    onClick={scrollToRoles}
                  >
                    View Open Roles
                    <ArrowDown size={18} />
                  </button>
                </motion.div>
              </Col>
              <Col lg={5}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="hiring-wrap"
                >
                  <div className="hiring-badge">
                    <span className="careers-live-dot" />
                    Now hiring
                  </div>
                  <div className="hiring-panel">
                    <div className="hiring-grid">
                      {jobs.map((job, idx) => {
                        const Icon = job.icon;
                        const [shortName, shortSub] = getShortTitle(job);
                        return (
                          <motion.button
                            key={job.id}
                            type="button"
                            className="hiring-role"
                            aria-label={job.title}
                            onClick={() => showJob(job.id)}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.3 + idx * 0.07 }}
                            whileHover={{
                              y: -4,
                              transition: { duration: 0.2, delay: 0 },
                            }}
                          >
                            <span className="hiring-role-icon">
                              <Icon size={18} />
                            </span>
                            <span className="hiring-role-body">
                              <span className="hiring-role-title">
                                {shortName}
                              </span>
                              <span className="hiring-role-sub">{shortSub}</span>
                            </span>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="careers-roles" id="open-roles">
          <div className="careers-dot-grid" />
          <Container>
            <div className="careers-roles-head">
              <div>
                <h2 className="careers-section-title">Open Roles</h2>
                <p className="careers-section-sub">
                  Select a role to see the full details and apply.
                </p>
              </div>
              <div
                className="careers-filters"
                role="group"
                aria-label="Filter roles by experience"
              >
                {filters.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`careers-filter${
                      filter === item.id ? " active" : ""
                    }`}
                    aria-pressed={filter === item.id}
                    onClick={() => setFilter(item.id)}
                  >
                    {item.label}
                    <span className="careers-filter-count">
                      {jobs.filter((job) => matchesFilter(job, item.id)).length}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="job-list-stack">
              {jobs
                .filter((job) => matchesFilter(job, filter))
                .map((job, idx) => {
                  const Icon = job.icon;
                  const isOpen = openIds.includes(job.id);
                  return (
                    <motion.article
                      key={job.id}
                      id={`job-${job.id}`}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.05 }}
                      transition={{ duration: 0.45, delay: idx * 0.04 }}
                      className={`job-card${isOpen ? " open" : ""}`}
                    >
                      <h3 className="job-heading">
                        <button
                          type="button"
                          className="job-toggle"
                          aria-expanded={isOpen}
                          aria-controls={`job-panel-${job.id}`}
                          onClick={() => toggleJob(job.id)}
                        >
                          <span className="job-icon">
                            <Icon size={26} />
                          </span>
                          <span className="job-toggle-body">
                            <span className="job-title">{job.title}</span>
                            <span className="job-meta">
                              {isFresher(job) ? (
                                <span className="job-meta-item fresher">
                                  <GraduationCap size={14} />
                                  Fresher
                                </span>
                              ) : null}
                              <span className="job-meta-item">
                                <MapPin size={14} />
                                {job.location}
                              </span>
                              {job.meta?.map((item) => {
                                const MetaIcon = metaIcons[item.label];
                                return (
                                  <span
                                    key={item.label}
                                    className="job-meta-item"
                                  >
                                    <MetaIcon size={14} />
                                    {item.label}: {item.value}
                                  </span>
                                );
                              })}
                            </span>
                          </span>
                          <span className="job-chevron">
                            <ChevronDown size={20} />
                          </span>
                        </button>
                      </h3>

                      <div className="job-panel" id={`job-panel-${job.id}`}>
                        <div className="job-panel-inner">
                          <div className="job-details">
                            <p className="job-intro job-summary">{job.intro}</p>

                            <div className="job-columns">
                              <div>
                                <div className="job-block-title">
                                  <span className="job-block-icon">
                                    <ListChecks size={17} />
                                  </span>
                                  Key Responsibilities
                                </div>
                                <ul className="job-timeline">
                                  {job.responsibilities.map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              </div>

                              <div>
                                <div className="job-block">
                                  <div className="job-block-title">
                                    <span className="job-block-icon">
                                      <Target size={17} />
                                    </span>
                                    {job.requirementsTitle || "Requirements"}
                                  </div>
                                  <ul className="job-tiles">
                                    {job.requirements.map((item) => (
                                      <li key={item}>
                                        <CheckCircle2 size={15} />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {job.preferredSkills?.length ? (
                                  <div className="job-block">
                                    <div className="job-block-title">
                                      <span className="job-block-icon">
                                        <Star size={17} />
                                      </span>
                                      Preferred Skills
                                    </div>
                                    <ul className="job-pills">
                                      {job.preferredSkills.map((item) => (
                                        <li key={item}>{item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>

                              <aside className="job-aside">
                                {job.qualification ? (
                                  <div className="job-info-card">
                                    <div className="job-block-title">
                                      <span className="job-block-icon">
                                        <GraduationCap size={17} />
                                      </span>
                                      Qualification
                                    </div>
                                    <p className="job-intro">
                                      {job.qualification}
                                    </p>
                                  </div>
                                ) : null}

                                {job.eligibility ? (
                                  <div className="job-info-card">
                                    <div className="job-block-title">
                                      <span className="job-block-icon">
                                        <BadgeCheck size={17} />
                                      </span>
                                      Eligibility
                                    </div>
                                    <p className="job-intro">
                                      {job.eligibility}
                                    </p>
                                  </div>
                                ) : null}

                                <div className="job-contact">
                                  <div className="job-contact-lead">
                                    Interested candidates can share their
                                    resumes at:
                                  </div>
                                  {job.contact?.title ? (
                                    <div>{job.contact.title}</div>
                                  ) : null}
                                  {job.contact?.company ? (
                                    <div>{job.contact.company}</div>
                                  ) : null}
                                  {job.contact?.addressLines?.map((line) => (
                                    <div key={line}>{line}</div>
                                  ))}
                                  {job.contact?.emails?.map((email) => (
                                    <div key={email}>
                                      <a href={`mailto:${email}`}>{email}</a>
                                    </div>
                                  ))}
                                  {job.contact?.phone ? (
                                    <div>
                                      <a href={`tel:${job.contact.phone}`}>
                                        {job.contact.phone}
                                      </a>
                                    </div>
                                  ) : null}
                                  {job.contact?.emails?.length ? (
                                    <a
                                      className="apply-btn"
                                      href={getApplyHref(job)}
                                    >
                                      Apply via Email
                                      <ArrowUpRight size={18} />
                                    </a>
                                  ) : null}
                                </div>
                              </aside>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
            </div>
          </Container>
        </section>

        <section className="careers-steps">
          <div className="careers-dot-grid" />
          <Container className="careers-steps-inner">
            <h2 className="careers-section-title">How to Apply</h2>
            <div className="steps-track">
              <div className="steps-line" aria-hidden="true">
                <span className="steps-line-dot" />
              </div>
              {steps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45, delay: idx * 0.12 }}
                    className="step-item"
                  >
                    <div className="step-node">
                      <Icon size={28} />
                    </div>
                    <div className="step-card">
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-text">{step.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div className="steps-cta">
              <button
                type="button"
                className="careers-cta"
                onClick={scrollToRoles}
              >
                View Open Roles
                <ArrowUp size={18} />
              </button>
            </div>
          </Container>
        </section>
      </div>
    </MotionConfig>
  );
};

export default Careers;
